/**
 * Libellé humain d'un périmètre (chantier C8, vague 2 ; C21 SP5, étape 4).
 *
 * Le périmètre GLOBAL est une commodité technique : il reçoit les captures
 * rapides (C4) et, depuis la vague 2, toute note créée sans périmètre. Son
 * nom de code n'a rien à faire à l'écran du lecteur amateur : il s'affiche
 * « Général », toujours en tête des listes, avec la mention « notes non
 * classées » (docs/VOCABULAIRE_ECRAN.md § 3.3). Les autres périmètres gardent
 * le nom que l'utilisateur leur a donné.
 */

import { GLOBAL_PERIMETRE_NAME } from '../constants';

/** Libellé affiché pour le périmètre GLOBAL. */
export const GLOBAL_PERIMETRE_LABEL = 'Général';

/** Mention courte qui accompagne « Général » dans les listes et sélecteurs. */
export const GLOBAL_PERIMETRE_MENTION = 'notes non classées';

/**
 * Description montrée pour Général à la place de celle que le serveur
 * enregistre (« Périmètre général — … ») : la donnée ne change pas.
 */
export const GLOBAL_PERIMETRE_DESCRIPTION = 'Notes non classées';

/** Vrai pour le périmètre implicite GLOBAL (valeur exacte du modèle). */
export function isGlobalPerimetre(name: string | undefined | null): boolean {
  return name === GLOBAL_PERIMETRE_NAME;
}

export function perimetreDisplayName(name: string | undefined | null): string {
  if (!name) return '';
  return isGlobalPerimetre(name) ? GLOBAL_PERIMETRE_LABEL : name;
}

/** Le code GLOBAL seul : ni lettre ni chiffre accolés (casse exacte, « GLOBALE » et « GLOBAL2 » exclus). */
const CODE_GENERAL = new RegExp(`(?<![\\p{L}\\p{N}])${GLOBAL_PERIMETRE_NAME}(?![\\p{L}\\p{N}])`, 'gu');

/**
 * Texte libre (titre, résumé d'une proposition de l'Assistant) où le code GLOBAL devient
 * « Général », à l'affichage seulement. Le reste du texte n'est pas réécrit.
 */
export function sansCodeGeneral(texte: string): string {
  return texte.replace(CODE_GENERAL, GLOBAL_PERIMETRE_LABEL);
}

/**
 * Dossier d'une note tel que l'écran le nomme : une note sans dossier est rangée
 * dans Général (« Sans périmètre » n'existe plus, VOCABULAIRE_ECRAN.md § 3.3).
 */
export function noteDossierLabel(name: string | undefined | null): string {
  return perimetreDisplayName(name) || GLOBAL_PERIMETRE_LABEL;
}

/**
 * Copie de `items` où Général passe en tête ; les autres gardent leur ordre
 * (tri stable, le tableau reçu n'est pas modifié).
 */
export function sortGlobalFirst<T>(items: ReadonlyArray<T>, getName: (item: T) => string | undefined | null): T[] {
  const general: T[] = [];
  const autres: T[] = [];
  items.forEach((item) => (isGlobalPerimetre(getName(item)) ? general : autres).push(item));
  return [...general, ...autres];
}

/** Noms réservés, comparés sans casse ni espaces de bord. */
const NOMS_RESERVES = new Set([GLOBAL_PERIMETRE_LABEL, GLOBAL_PERIMETRE_NAME].map((n) => n.toLocaleLowerCase('fr')));

/** Caractères sans chasse (espaces de largeur nulle, liants, BOM). */
const SANS_CHASSE = /[\u200B-\u200D\uFEFF]/g;

/**
 * Vrai si un dossier ne peut pas porter ce nom : « Général » et « GLOBAL »,
 * casse et espaces ignorés — il n'y a jamais deux « Général » à l'écran.
 * Noms trompeurs : normalisé en NFC et débarrassé des caractères sans chasse
 * avant le trim (même règle que l'API, `api/src/utils/reservedDossierName.ts`).
 */
export function isReservedDossierName(name: string | undefined | null): boolean {
  if (!name) return false;
  const nom = name.normalize('NFC').replace(SANS_CHASSE, '').trim();
  return NOMS_RESERVES.has(nom.toLocaleLowerCase('fr'));
}
